/**
 * Authored checklists — CD02 Modules 5 and 6.
 *
 * WHY THIS EXISTS AT ALL
 *
 * Five lessons in this course do their work OUTSIDE the course, and they are the only
 * five: Module 6's V2602 ("Download and install the Claude desktop app") and V2611
 * ("Open your own app in Claude Code"), and Module 5's V2511, V2513 and V2514, which
 * ship the student's app to GitHub and publish it. The student leaves, checks a plan,
 * downloads an installer, makes a folder, drags a file into it, clicks around someone
 * else's website — and may not come back. Nothing we render can verify any of that.
 *
 * Everything below is written about V2602, which came first. The other four are the
 * same widget for the same reason; what differs is what each branches ON — see
 * PROJECT_SETUP for V2611 and the Module 5 block near the bottom for the rest.
 *
 * It used to be a drag-to-order canvas: put the five install steps in sequence. That
 * was the wrong exercise twice over. The answer was printed, numbered and in order,
 * in the lesson pane six inches to the left, so it graded transcription. And ordering
 * is not the skill — nobody fails to install an app because they did the steps in the
 * wrong order. The real failure modes are the three the prose already names:
 * installing before checking you have a paid plan, grabbing the wrong installer, and
 * on Windows not having Git.
 *
 * So this is NOT a test. It is a checklist the student ticks as they actually do it,
 * and it says so on screen. What it buys, which a canvas could not:
 *
 *   1. It BRANCHES ON THE OPERATING SYSTEM. The Git-for-Windows step is the single
 *      most common place to get stuck, and the error it produces never says "install
 *      Git". As a paragraph it gets skimmed; as a step in your own list it does not.
 *   2. It SURVIVES THE APP SWITCH. Every other lesson is finished in one sitting.
 *      This one is interrupted by an installer, so the state is persisted and the
 *      student comes back to where they were.
 *   3. It GATES ON THE PLAN CHECK, because doing that one second is what the prose
 *      calls "a bad twenty minutes".
 *
 * KEEP IN STEP WITH THE PROSE. Every step below is one heading of the authored
 * lesson body in backend/src/data/claudeCodeLessonProse.js (V2602). When the real
 * install flow changes, that file and this one move together — same rule as the
 * replica screens in starterTemplate/authoredLessons.js, and all three name each
 * other.
 *
 * SHAPE
 *
 *   { intro, selfAttested, osLabel, alreadyDone, alreadyDoneHint, gateNote,
 *     os:    [{ id, label, icon }],
 *     steps: [{ id, icon, title, body, os?, note?, gate? }],
 *     exercise, hint }
 *
 * `step.os` limits a step to those platforms; absent means all of them. `step.body`
 * is either a string or an object keyed by platform id, so one step can say the right
 * thing on each. `gate: true` marks the step that must be ticked before the rest
 * unlock — there is exactly one.
 *
 * `os` IS A BRANCH, NOT LITERALLY AN OPERATING SYSTEM. V2602 branches on the student's
 * platform because that is what changes its steps; V2611 branches on whether they still
 * have their `index.html`. The mechanic is identical — pick one of N variants, get the
 * steps and wording for it — so the field was reused rather than duplicated. It keeps
 * its name because renaming it would orphan every `{os, done}` blob already written to
 * localStorage under `checklist:V2602`. Read it as "which version of this list is
 * yours".
 *
 * Never give a checklist an EMPTY `os` list to mean "no branch": `stepsForOs` would
 * still return the unscoped steps, so it renders and looks fine, and the student's
 * ticks save correctly only because InstallChecklist's write effect no longer waits on
 * a selection. Give it a real branch or give it two options.
 *
 * Ids are identifiers, not words: they key the saved progress and must be IDENTICAL
 * across locales, exactly as in authoredLessons.js. Only visible copy translates.
 */

const INSTALL_CLAUDE_CODE = {
    en: {
        title: "Your install checklist",
        allDone: "All done — Claude Code is on your computer.",
        intro: "Tick each one as you actually do it. Your progress is saved, so you can leave, install, and come back to this page.",
        selfAttested: "Nothing here is checked automatically — this list is yours to keep track with, and you tick it honestly.",
        osLabel: "Which computer are you on?",
        alreadyDone: "I already have it installed",
        alreadyDoneHint: "Ticks everything. Only use it if the Code tab already opens for you.",
        gateNote: "Start with the plan check. Doing it second is what costs people twenty minutes.",
        os: [
            { id: "mac", label: "Mac", icon: "🍎" },
            { id: "windows", label: "Windows", icon: "🪟" },
            { id: "linux", label: "Linux", icon: "🐧" },
        ],
        steps: [
            {
                id: "plan",
                icon: "🔑",
                gate: true,
                title: "Check your plan first",
                body: "Claude Code works on a Pro, Max, Team or Enterprise plan. It does not work on the free plan. Check before you download anything — finding out afterwards, when you click the Code tab, is twenty minutes you do not get back.",
                note: "It is a separate subscription from this course. If you are not paying for one today, read the rest of the module anyway — none of it expires.",
            },
            {
                id: "download",
                icon: "⬇️",
                title: "Download the app",
                body: {
                    mac: "Go to claude.com/download and get the macOS build.",
                    windows: "Go to claude.com/download. Most computers need the x64 installer — but if yours is an ARM64 machine, and some newer laptops are, there is a separate ARM64 one.",
                    linux: "Go to claude.com/download and get the Linux beta — Ubuntu and Debian, installed with apt.",
                },
            },
            {
                id: "install",
                icon: "📦",
                title: "Install it, then sign in",
                body: {
                    mac: "Run the file you downloaded and follow the prompts, like any other app. Then open Claude from Applications and sign in with your Claude account.",
                    windows: "Run the file you downloaded and follow the prompts, like any other app. Then open Claude from the Start menu and sign in with your Claude account.",
                    linux: "Install the package you downloaded, then launch Claude and sign in with your Claude account.",
                },
            },
            {
                id: "git",
                icon: "🧩",
                os: ["windows"],
                title: "Windows only: install Git, then restart Claude",
                body: "The Code tab cannot touch files on your computer until Git for Windows is installed. Get it from git-scm.com/downloads/win, install it, and then restart the Claude app — it only looks for Git at startup.",
                note: "This is where most Windows installs get stuck, and the error you get does not say \"install Git\" in plain words. Now you know.",
            },
            {
                id: "codetab",
                icon: "🗂️",
                title: "Open the Code tab",
                body: "Along the top of the window are three tabs. Click Code. That tab is Claude Code, and it is the only one that can reach the files on your computer.",
                note: "If clicking Code asks you to upgrade, your plan is not a paid one — back to the first step. If it asks you to sign in again, do that and then restart the app.",
            },
            {
                id: "confirm",
                icon: "✅",
                title: "Confirm it works",
                body: "Ask it something small — anything at all — and watch it answer. That reply is proof the install worked and you are signed in.",
            },
        ],
        exercise: "Install Claude Code on your own computer, using the checklist beside this lesson. Pick your operating system at the top so the steps match your machine, and tick each one as you finish it.",
        hint: "If something goes wrong, the error message is the useful part: copy it, paste it to Claude in the Prompt Lab below, and ask what it means. That is the Module 2 skill, and this is a real chance to use it.",
    },

    es: {
        title: "Tu lista de instalación",
        allDone: "Listo: ya tienes Claude Code en tu computadora.",
        intro: "Ve marcando cada paso conforme lo haces de verdad. Tu progreso se guarda, así que puedes salir, instalar y volver a esta página.",
        selfAttested: "Aquí no se revisa nada automáticamente: esta lista es tuya, para llevar la cuenta, y la marcas con honestidad.",
        osLabel: "¿Qué computadora usas?",
        alreadyDone: "Ya la tengo instalada",
        alreadyDoneHint: "Marca todo. Úsalo solo si la pestaña Code ya te abre.",
        gateNote: "Empieza por revisar tu plan. Dejarlo para después es justo lo que le cuesta veinte minutos a la gente.",
        os: [
            { id: "mac", label: "Mac", icon: "🍎" },
            { id: "windows", label: "Windows", icon: "🪟" },
            { id: "linux", label: "Linux", icon: "🐧" },
        ],
        steps: [
            {
                id: "plan",
                icon: "🔑",
                gate: true,
                title: "Revisa tu plan primero",
                body: "Claude Code funciona con un plan Pro, Max, Team o Enterprise. No funciona con el plan gratuito. Revísalo antes de descargar nada: enterarte después, al hacer clic en la pestaña Code, son veinte minutos que no recuperas.",
                note: "Es una suscripción aparte de este curso. Si hoy no vas a pagarla, lee el resto del módulo de todos modos: nada de esto caduca.",
            },
            {
                id: "download",
                icon: "⬇️",
                title: "Descarga la aplicación",
                body: {
                    mac: "Entra a claude.com/download y consigue la versión para macOS.",
                    windows: "Entra a claude.com/download. La mayoría de las computadoras necesitan el instalador x64, pero si la tuya es ARM64 —algunas laptops nuevas lo son— hay uno aparte.",
                    linux: "Entra a claude.com/download y consigue la beta de Linux: Ubuntu y Debian, que se instala con apt.",
                },
            },
            {
                id: "install",
                icon: "📦",
                title: "Instálala y luego inicia sesión",
                body: {
                    mac: "Ejecuta el archivo que descargaste y sigue los pasos, como con cualquier aplicación. Después abre Claude desde Aplicaciones y entra con tu cuenta de Claude.",
                    windows: "Ejecuta el archivo que descargaste y sigue los pasos, como con cualquier aplicación. Después abre Claude desde el menú Inicio y entra con tu cuenta de Claude.",
                    linux: "Instala el paquete que descargaste, después abre Claude y entra con tu cuenta de Claude.",
                },
            },
            {
                id: "git",
                icon: "🧩",
                os: ["windows"],
                title: "Solo en Windows: instala Git y reinicia Claude",
                body: "La pestaña Code no puede tocar los archivos de tu computadora hasta que instales Git for Windows. Consíguelo en git-scm.com/downloads/win, instálalo y después reinicia la aplicación de Claude: solo busca Git al arrancar.",
                note: "Aquí es donde se atora la mayoría de las instalaciones en Windows, y el error que aparece no dice «instala Git» con esas palabras. Ahora ya lo sabes.",
            },
            {
                id: "codetab",
                icon: "🗂️",
                title: "Abre la pestaña Code",
                body: "Arriba de la ventana hay tres pestañas. Haz clic en Code. Esa pestaña es Claude Code, y es la única que puede llegar a los archivos de tu computadora.",
                note: "Si al hacer clic en Code te pide mejorar tu plan, tu plan no es de pago: vuelve al primer paso. Si te pide iniciar sesión otra vez, hazlo y después reinicia la aplicación.",
            },
            {
                id: "confirm",
                icon: "✅",
                title: "Comprueba que funciona",
                body: "Pregúntale algo pequeño, lo que sea, y mira cómo responde. Esa respuesta es la prueba de que la instalación salió bien y de que tu sesión está iniciada.",
            },
        ],
        exercise: "Instala Claude Code en tu propia computadora usando la lista que está junto a esta lección. Elige arriba tu sistema operativo para que los pasos coincidan con tu máquina, y ve marcando cada uno conforme lo terminas.",
        hint: "Si algo sale mal, el mensaje de error es la parte útil: cópialo, pégaselo a Claude en el Prompt Lab de abajo y pregúntale qué significa. Es la habilidad del Módulo 2, y esta es una oportunidad real de usarla.",
    },
};

/**
 * V2611 — "Open your own app in Claude Code". The doing half of V2604.
 *
 * V2604 shows the new-session screen and asks the student to identify Local, Cloud, SSH
 * and Select folder. That teaches the fork, and it verifies nothing: the actual work —
 * make a folder, move a file into it, point the app at it — happens in Finder or
 * Explorer, in another application, and the prose was the only thing walking them
 * through it. This is that walk, ticked.
 *
 * IT BRANCHES ON WHETHER THEY STILL HAVE THEIR FILE, and that is the whole reason it
 * needed to be a checklist rather than three more paragraphs. V2604's prose opens with
 * "back in Module 4 you pressed Download my app and your browser saved index.html",
 * which is false for a large number of students: Web Lab code is never persisted, so
 * anyone who did not press the button at the time cannot go back and get their app —
 * that lesson now rebuilds from the authored scaffold. Told "find your index.html",
 * they hunt for a file that does not exist, on step one of the lesson that is supposed
 * to be the payoff of the whole course.
 *
 * So the `fresh` branch is not a courtesy path. It is the one a good share of students
 * are on, and it has to arrive at the same place: an empty folder is a perfectly good
 * project, and asking Claude Code to write the first version into it demonstrates the
 * tool at least as well as reading a file back does.
 *
 * The gate is that choice, because every step after it assumes an answer, and finding
 * out at Select folder that you pointed at an empty folder you meant to fill is the
 * same twenty wasted minutes the V2602 gate exists to prevent.
 */
const PROJECT_SETUP = {
    en: {
        title: "Your setup checklist",
        allDone: "All done — your app is open in Claude Code.",
        intro: "Tick each one as you actually do it. Your progress is saved, so you can leave, do the step on your computer, and come back to this page.",
        selfAttested: "Nothing here is checked automatically — this list is yours to keep track with, and you tick it honestly.",
        osLabel: "Where are you starting from?",
        alreadyDone: "My app is already open in Claude Code",
        alreadyDoneHint: "Ticks everything. Only use it if Claude Code is already pointed at your project folder.",
        gateNote: "Settle this first. Every step below assumes you know which of the two you are doing.",
        os: [
            { id: "have", label: "I have my app download", icon: "📦" },
            { id: "fresh", label: "I don't have it", icon: "✨" },
        ],
        steps: [
            {
                id: "start",
                icon: "🔎",
                gate: true,
                title: "Work out what you are starting from",
                body: {
                    have: "Open your Downloads folder and look for a **.zip** named after your app — that is what the **Download my app** button saved back in Module 4. If you downloaded it more than once, the newest one is your latest version.\n\nDownloaded it a long time ago and got a plain `index.html` instead of a zip? That still works. Skip the unzipping in the next step and just move that one file into the folder you make.",
                    fresh: "You do not need a file. You will make an empty folder and have Claude Code write the first version of your app into it. Only this step and the last one change — everything in between is the same.",
                },
                note: "Spent two minutes looking and it is not there? Switch to \"I don't have it\" above. That path works, and hunting for a file you never downloaded is the worst way to start this lesson.",
            },
            {
                id: "folder",
                icon: "📁",
                title: {
                    have: "Unzip it — properly",
                    fresh: "Make a folder just for this app",
                },
                body: {
                    have: "**Right-click the zip → Extract All** (on a Mac, double-click it). You get a folder named after your app with about eleven files inside: `index.html`, `app.html`, `config.js` and the rest. Move that folder somewhere you will find it again — your Desktop is fine.",
                    fresh: "Somewhere you will find it again — your Desktop is fine. Name it after your app, in lower case with a dash instead of a space: `my-app`, not `My App`. Leave it empty — Claude Code puts the first file in it for you.",
                },
                note: {
                    have: "Do NOT skip the extracting. On Windows, double-clicking a zip shows you what is inside without unpacking it, and an app run from in there saves your data into a temporary folder that gets wiped. If the window you are looking at still has the zip's name in the address bar, you have not extracted it yet.",
                    fresh: "Claude only ever sees inside this folder. That is what makes a folder of its own the safe thing to do, and pointing it at your Desktop or your whole user folder the unsafe one.",
                },
            },
            {
                id: "session",
                icon: "🗂️",
                title: "Start a new session in the Code tab",
                body: "Open the Claude desktop app, click **Code** along the top, then **+ New session**. This is the screen from the last lesson.",
            },
            {
                id: "local",
                icon: "💻",
                title: "Choose Local",
                body: "It asks where the session should run. **Local** is the one you want — your machine, your files, the folder from step 2. Cloud, SSH and WSL all run somewhere your folder is not.",
            },
            {
                id: "select",
                icon: "📂",
                title: "Click Select folder, and choose your project folder",
                body: "A **Select folder** button appears. Click it, find the folder from step 2, and choose it. That is the whole of \"adding a project\" — no import, no sign-up, no config file.",
                note: "Check the folder name on screen before you type anything. If that is wrong, everything after it is wrong.",
            },
            {
                id: "ask",
                icon: "💬",
                title: "Ask it the first question",
                body: {
                    have: "Type: **what does this project do?** Claude opens your project, reads the `CLAUDE.md` your build spec was written into, and describes your own app back to you. Nobody pasted anything in — it went and looked.",
                    fresh: "Type: **build me a starting page for** and finish the sentence with what your app is, in your own words. Watch it create `index.html` in your folder. Open that file in your browser afterwards: that is your app, on your computer.",
                },
            },
        ],
        exercise: "Open your own app in Claude Code, using the checklist beside this lesson. Choose at the top whether you still have your app download or are starting fresh — both paths finish in the same place — and tick each step as you go.",
        hint: "If Claude says it cannot find any files, the folder is nearly always the reason. Check that the folder name on screen is the one you extracted, and that the files are directly inside it rather than inside a second folder of the same name.",
    },

    es: {
        title: "Tu lista de preparación",
        allDone: "Listo: tu app ya está abierta en Claude Code.",
        intro: "Ve marcando cada paso conforme lo haces de verdad. Tu progreso se guarda, así que puedes salir, hacer el paso en tu computadora y volver a esta página.",
        selfAttested: "Aquí no se revisa nada automáticamente: esta lista es tuya, para llevar la cuenta, y la marcas con honestidad.",
        osLabel: "¿Desde dónde empiezas?",
        alreadyDone: "Mi app ya está abierta en Claude Code",
        alreadyDoneHint: "Marca todo. Úsalo solo si Claude Code ya apunta a la carpeta de tu proyecto.",
        gateNote: "Resuelve esto primero. Todos los pasos de abajo dan por hecho que ya sabes cuál de los dos casos es el tuyo.",
        os: [
            { id: "have", label: "Tengo la descarga de mi app", icon: "📦" },
            { id: "fresh", label: "No la tengo", icon: "✨" },
        ],
        steps: [
            {
                id: "start",
                icon: "🔎",
                gate: true,
                title: "Averigua desde dónde empiezas",
                body: {
                    have: "Abre tu carpeta de Descargas y busca un **.zip** con el nombre de tu app: eso es lo que guardó el botón **Descargar mi app** allá en el Módulo 4. Si lo descargaste varias veces, el más reciente es tu última versión.\n\n¿Lo descargaste hace mucho y en vez de un zip tienes un `index.html` solo? También sirve. Sáltate lo de descomprimir en el siguiente paso y nada más mueve ese archivo a la carpeta que crees.",
                    fresh: "No necesitas ningún archivo. Vas a crear una carpeta vacía y a pedirle a Claude Code que escriba ahí dentro la primera versión de tu app. Solo cambian este paso y el último: todo lo de en medio es igual.",
                },
                note: "¿Ya buscaste dos minutos y no aparece? Cámbiate arriba a «No la tengo». Ese camino funciona, y ponerte a cazar un archivo que nunca descargaste es la peor forma de empezar esta lección.",
            },
            {
                id: "folder",
                icon: "📁",
                title: {
                    have: "Descomprímelo — de verdad",
                    fresh: "Crea una carpeta solo para esta app",
                },
                body: {
                    have: "**Clic derecho en el zip → Extraer todo** (en Mac, doble clic). Te queda una carpeta con el nombre de tu app y unos once archivos adentro: `index.html`, `app.html`, `config.js` y los demás. Mueve esa carpeta a donde la vuelvas a encontrar; tu Escritorio está bien.",
                    fresh: "En algún lugar donde la vuelvas a encontrar; tu Escritorio está bien. Ponle el nombre de tu app, en minúsculas y con guion en vez de espacio: `mi-app`, no `Mi App`. Déjala vacía: Claude Code pone ahí el primer archivo por ti.",
                },
                note: {
                    have: "NO te saltes lo de extraer. En Windows, dar doble clic en un zip te deja ver lo que hay adentro sin descomprimirlo, y una app abierta desde ahí guarda tus datos en una carpeta temporal que después se borra. Si la ventana que estás viendo todavía tiene el nombre del zip en la barra de direcciones, no lo has extraído.",
                    fresh: "Claude solo ve lo que está dentro de esta carpeta. Por eso darle una carpeta propia es lo seguro, y apuntarlo a tu Escritorio o a toda tu carpeta de usuario es lo inseguro.",
                },
            },
            {
                id: "session",
                icon: "🗂️",
                title: "Abre una sesión nueva en la pestaña Code",
                body: "Abre la aplicación de escritorio de Claude, haz clic en **Code** arriba y después en **+ New session**. Esta es la pantalla de la lección anterior.",
            },
            {
                id: "local",
                icon: "💻",
                title: "Elige Local",
                body: "Te pregunta dónde debe correr la sesión. **Local** es la que quieres: tu máquina, tus archivos, la carpeta del paso 2. Cloud, SSH y WSL corren en lugares donde tu carpeta no está.",
            },
            {
                id: "select",
                icon: "📂",
                title: "Haz clic en Select folder y elige la carpeta de tu proyecto",
                body: "Aparece un botón **Select folder** («seleccionar carpeta»). Haz clic, busca la carpeta del paso 2 y elígela. Eso es todo lo que significa «agregar un proyecto»: sin importación, sin registro, sin archivo de configuración.",
                note: "Revisa en pantalla el nombre de la carpeta antes de escribir nada. Si esa está mal, todo lo que venga después está mal.",
            },
            {
                id: "ask",
                icon: "💬",
                title: "Hazle la primera pregunta",
                body: {
                    have: "Escribe: **¿qué hace este proyecto?** Claude abre tu proyecto, lee el `CLAUDE.md` donde quedó escrito tu plan de construcción y te describe tu propia app. Nadie le pegó nada: fue y lo revisó.",
                    fresh: "Escribe: **créame una página inicial para** y termina la frase diciendo qué es tu app, con tus palabras. Míralo crear `index.html` en tu carpeta. Después abre ese archivo en tu navegador: esa es tu app, en tu computadora.",
                },
            },
        ],
        exercise: "Abre tu propia app en Claude Code usando la lista que está junto a esta lección. Elige arriba si todavía tienes la descarga de tu app o si empiezas de cero —ambos caminos terminan en el mismo lugar— y ve marcando cada paso conforme avanzas.",
        hint: "Si Claude dice que no encuentra archivos, casi siempre es por la carpeta. Revisa que el nombre que aparece en pantalla sea el de la carpeta que extrajiste, y que los archivos estén directamente adentro y no dentro de otra carpeta con el mismo nombre.",
    },
};

/* ── CD04 — the same lesson, pointed at a different download ─────────────────
 *
 * The ONE checklist CD04 does not alias, and it is two sentences that make the
 * difference — but they are the two the student acts on first.
 *
 * PROJECT_SETUP's `have` branch says the zip came from the **Download my app**
 * button "back in Module 4", and offers a fallback for anyone who got a plain
 * `index.html` from it instead. Neither is true in CD04: that course has no Web Lab
 * build module and no such button. Its zip comes from the BUILD SPEC lesson four
 * lessons earlier in this same module, the `downloadOnPerfect` reward — the same
 * eleven-file project folder, with the spec in its CLAUDE.md, which is why every
 * other step here carries over word for word.
 *
 * Sending a student to look in the wrong place for a file they do have is worse
 * than sending them to look for one they don't: they find nothing, and the obvious
 * conclusion is that they missed a lesson.
 *
 * BOTH BRANCHES STAY LIVE. `downloadOnPerfect` only fires on a full slate, so a
 * student whose spec came back short reaches this lesson with nothing, and the
 * `fresh` path is how they arrive at the same place. `have` is first because
 * `guessOs` matches none of these ids and falls through to `ids[0]`.
 */
const PROJECT_SETUP_V4 = {
    en: {
        title: "Your setup checklist",
        allDone: "All done — your app is open in Claude Code.",
        intro: "Tick each one as you actually do it. Your progress is saved, so you can leave, do the step on your computer, and come back to this page.",
        selfAttested: "Nothing here is checked automatically — this list is yours to keep track with, and you tick it honestly.",
        osLabel: "Where are you starting from?",
        alreadyDone: "My app is already open in Claude Code",
        alreadyDoneHint: "Ticks everything. Only use it if Claude Code is already pointed at your project folder.",
        gateNote: "Settle this first. Every step below assumes you know which of the two you are doing.",
        os: [
            { id: "have", label: "I have my project download", icon: "📦" },
            { id: "fresh", label: "I don't have it", icon: "✨" },
        ],
        steps: [
            {
                id: "start",
                icon: "🔎",
                gate: true,
                title: "Work out what you are starting from",
                body: {
                    have: "Open your Downloads folder and look for a **.zip** named after your app — that is what you downloaded at the end of the build spec lesson, four lessons ago. If you downloaded it more than once, the newest one is your latest version.",
                    fresh: "You do not need a file. You will make an empty folder and have Claude Code write the first version of your app into it. Only this step and the last one change — everything in between is the same.",
                },
                note: "No zip? Then your build spec did not come back complete, and the download only appears when it does. Nothing is lost: choose \"I don't have it\" above. That path works, and hunting for a file you never earned is the worst way to start this lesson.",
            },
            {
                id: "folder",
                icon: "📁",
                title: {
                    have: "Unzip it — properly",
                    fresh: "Make a folder just for this app",
                },
                body: {
                    have: "**Right-click the zip → Extract All** (on a Mac, double-click it). You get a folder named after your app with about eleven files inside: `index.html`, `app.html`, `config.js` and the rest. Move that folder somewhere you will find it again — your Desktop is fine.",
                    fresh: "Somewhere you will find it again — your Desktop is fine. Name it after your app, in lower case with a dash instead of a space: `my-app`, not `My App`. Leave it empty — Claude Code puts the first file in it for you.",
                },
                note: {
                    have: "Do NOT skip the extracting. On Windows, double-clicking a zip shows you what is inside without unpacking it, and an app run from in there saves your data into a temporary folder that gets wiped. If the window you are looking at still has the zip's name in the address bar, you have not extracted it yet.",
                    fresh: "Claude only ever sees inside this folder. That is what makes a folder of its own the safe thing to do, and pointing it at your Desktop or your whole user folder the unsafe one.",
                },
            },
            {
                id: "session",
                icon: "🗂️",
                title: "Start a new session in the Code tab",
                body: "Open the Claude desktop app, click **Code** along the top, then **+ New session**. This is the screen from the last lesson.",
            },
            {
                id: "local",
                icon: "💻",
                title: "Choose Local",
                body: "It asks where the session should run. **Local** is the one you want — your machine, your files, the folder from step 2. Cloud, SSH and WSL all run somewhere your folder is not.",
            },
            {
                id: "select",
                icon: "📂",
                title: "Click Select folder, and choose your project folder",
                body: "A **Select folder** button appears. Click it, find the folder from step 2, and choose it. That is the whole of \"adding a project\" — no import, no sign-up, no config file.",
                note: "Check the folder name on screen before you type anything. If that is wrong, everything after it is wrong.",
            },
            {
                id: "ask",
                icon: "💬",
                title: "Ask it the first question",
                body: {
                    have: "Type: **what does this project do?** Claude opens your project, reads the `CLAUDE.md` your build spec was written into, and describes your own app back to you. Nobody pasted anything in — it went and looked.",
                    fresh: "Type: **build me a starting page for** and finish the sentence with what your app is, in your own words. Watch it create `index.html` in your folder. Open that file in your browser afterwards: that is your app, on your computer.",
                },
            },
        ],
        exercise: "Open your own app in Claude Code, using the checklist beside this lesson. Choose at the top whether you have your project download from the build spec lesson or are starting fresh — both paths finish in the same place — and tick each step as you go.",
        hint: "If Claude says it cannot find any files, the folder is nearly always the reason. Check that the folder name on screen is the one you extracted, and that the files are directly inside it rather than inside a second folder of the same name.",
    },

    es: {
        title: "Tu lista de preparación",
        allDone: "Listo: tu app ya está abierta en Claude Code.",
        intro: "Ve marcando cada paso conforme lo haces de verdad. Tu progreso se guarda, así que puedes salir, hacer el paso en tu computadora y volver a esta página.",
        selfAttested: "Aquí no se revisa nada automáticamente: esta lista es tuya, para llevar la cuenta, y la marcas con honestidad.",
        osLabel: "¿Desde dónde empiezas?",
        alreadyDone: "Mi app ya está abierta en Claude Code",
        alreadyDoneHint: "Marca todo. Úsalo solo si Claude Code ya apunta a la carpeta de tu proyecto.",
        gateNote: "Resuelve esto primero. Todos los pasos de abajo dan por hecho que ya sabes cuál de los dos casos es el tuyo.",
        os: [
            { id: "have", label: "Tengo la descarga de mi proyecto", icon: "📦" },
            { id: "fresh", label: "No la tengo", icon: "✨" },
        ],
        steps: [
            {
                id: "start",
                icon: "🔎",
                gate: true,
                title: "Averigua desde dónde empiezas",
                body: {
                    have: "Abre tu carpeta de Descargas y busca un **.zip** con el nombre de tu app: eso es lo que descargaste al terminar la lección del plan de construcción, cuatro lecciones atrás. Si lo descargaste varias veces, el más reciente es tu última versión.",
                    fresh: "No necesitas ningún archivo. Vas a crear una carpeta vacía y a pedirle a Claude Code que escriba ahí dentro la primera versión de tu app. Solo cambian este paso y el último: todo lo de en medio es igual.",
                },
                note: "¿No hay zip? Entonces tu plan de construcción no quedó completo, y la descarga solo aparece cuando sí lo está. No perdiste nada: elige arriba «No la tengo». Ese camino funciona, y ponerte a cazar un archivo que nunca obtuviste es la peor forma de empezar esta lección.",
            },
            {
                id: "folder",
                icon: "📁",
                title: {
                    have: "Descomprímelo — de verdad",
                    fresh: "Crea una carpeta solo para esta app",
                },
                body: {
                    have: "**Clic derecho en el zip → Extraer todo** (en Mac, doble clic). Te queda una carpeta con el nombre de tu app y unos once archivos adentro: `index.html`, `app.html`, `config.js` y los demás. Mueve esa carpeta a donde la vuelvas a encontrar; tu Escritorio está bien.",
                    fresh: "En algún lugar donde la vuelvas a encontrar; tu Escritorio está bien. Ponle el nombre de tu app, en minúsculas y con guion en vez de espacio: `mi-app`, no `Mi App`. Déjala vacía: Claude Code pone ahí el primer archivo por ti.",
                },
                note: {
                    have: "NO te saltes lo de extraer. En Windows, dar doble clic en un zip te deja ver lo que hay adentro sin descomprimirlo, y una app abierta desde ahí guarda tus datos en una carpeta temporal que después se borra. Si la ventana que estás viendo todavía tiene el nombre del zip en la barra de direcciones, no lo has extraído.",
                    fresh: "Claude solo ve lo que está dentro de esta carpeta. Por eso darle una carpeta propia es lo seguro, y apuntarlo a tu Escritorio o a toda tu carpeta de usuario es lo inseguro.",
                },
            },
            {
                id: "session",
                icon: "🗂️",
                title: "Abre una sesión nueva en la pestaña Code",
                body: "Abre la aplicación de escritorio de Claude, haz clic en **Code** arriba y después en **+ New session**. Esta es la pantalla de la lección anterior.",
            },
            {
                id: "local",
                icon: "💻",
                title: "Elige Local",
                body: "Te pregunta dónde debe correr la sesión. **Local** es la que quieres: tu máquina, tus archivos, la carpeta del paso 2. Cloud, SSH y WSL corren en lugares donde tu carpeta no está.",
            },
            {
                id: "select",
                icon: "📂",
                title: "Haz clic en Select folder y elige la carpeta de tu proyecto",
                body: "Aparece un botón **Select folder** («seleccionar carpeta»). Haz clic, busca la carpeta del paso 2 y elígela. Eso es todo lo que significa «agregar un proyecto»: sin importación, sin registro, sin archivo de configuración.",
                note: "Revisa en pantalla el nombre de la carpeta antes de escribir nada. Si esa está mal, todo lo que venga después está mal.",
            },
            {
                id: "ask",
                icon: "💬",
                title: "Hazle la primera pregunta",
                body: {
                    have: "Escribe: **¿qué hace este proyecto?** Claude abre tu proyecto, lee el `CLAUDE.md` donde quedó escrito tu plan de construcción y te describe tu propia app. Nadie le pegó nada: fue y lo revisó.",
                    fresh: "Escribe: **créame una página inicial para** y termina la frase diciendo qué es tu app, con tus palabras. Míralo crear `index.html` en tu carpeta. Después abre ese archivo en tu navegador: esa es tu app, en tu computadora.",
                },
            },
        ],
        exercise: "Abre tu propia app en Claude Code usando la lista que está junto a esta lección. Elige arriba si tienes la descarga de tu proyecto de la lección del plan de construcción o si empiezas de cero —ambos caminos terminan en el mismo lugar— y ve marcando cada paso conforme avanzas.",
        hint: "Si Claude dice que no encuentra archivos, casi siempre es por la carpeta. Revisa que el nombre que aparece en pantalla sea el de la carpeta que extrajiste, y que los archivos estén directamente adentro y no dentro de otra carpeta con el mismo nombre.",
    },
};

/* ── CD02 Module 5 — the three shipping checklists ───────────────────────────
 *
 * Same widget, same reason: Git, github.com and GitHub Pages all happen OUTSIDE this
 * page, on the student's own machine and on someone else's website, and nothing we
 * render can verify a word of it.
 *
 * THE BRANCH IS `claudecode` vs `browser`, and it is the same one in all three so the
 * student answers the same question the same way three times running. `claudecode` is
 * the intended path. `browser` does the entire module through github.com — web
 * uploader, web editor, Pages toggle — with no install, no terminal and no paid plan.
 *
 * That second path is not a courtesy. Claude Code needs a paid Claude plan; Module 6's
 * checklists are self-attested, so students without one complete it and arrive here
 * anyway. This module is where the course keeps its promise — your app is live at the
 * end — and that promise cannot be conditional on a subscription we do not sell.
 *
 * `claudecode` is FIRST in each `os` list because `guessOs` matches none of these ids
 * and falls through to `ids[0]`, so the first option is what an untouched checklist
 * shows.
 *
 * KEEP IN STEP WITH THE PROSE, as V2602 does: every step below is one `###` heading of
 * the authored body in backend/src/data/authoredLessonProse.js, in the same order. The
 * prose has two headings that are NOT steps — V2511's opening "pick how you are doing
 * this" is this widget's branch selector, and V2514's closing "what you actually have"
 * is the end of the course talking.
 *
 * The GitHub facts both files depend on are listed in one place, the comment above
 * V2511 in that file. When GitHub moves a button, those two files move together.
 */

const GITHUB_PUSH = {
    en: {
        title: "Your GitHub checklist",
        allDone: "All done — your code is on GitHub.",
        intro: "Tick each one as you actually do it. Your progress is saved, so you can leave, do the step, and come back to this page.",
        selfAttested: "Nothing here is checked automatically — this list is yours to keep track with, and you tick it honestly.",
        osLabel: "How are you doing this?",
        alreadyDone: "My app is already on GitHub",
        alreadyDoneHint: "Ticks everything. Only use it if your files are actually visible at github.com/your-username/your-repo.",
        gateNote: "Settle the folder first. If that is wrong, every step after it is wrong.",
        os: [
            { id: "claudecode", label: "Claude Code does it for me", icon: "🤖" },
            { id: "browser", label: "I'll use github.com", icon: "🌐" },
        ],
        steps: [
            {
                id: "folder",
                icon: "📁",
                gate: true,
                title: "Know which folder you are shipping",
                body: {
                    claudecode: "It is the folder Claude Code already has open — the one from the last module. Check it has an `index.html` sitting directly inside it, not tucked into another folder.",
                    browser: "It is your unzipped app folder, the one from the **Download my app** button. Open it and check there is an `index.html` sitting directly inside it, not tucked into another folder.",
                },
                note: "Do not have it, or cannot find it? An empty folder with a single `index.html` in it is enough for everything below — and Claude will write you one if you ask. Do not spend twenty minutes hunting.",
            },
            {
                id: "account",
                icon: "🐙",
                title: "Make a GitHub account",
                body: "Go to **github.com** and sign up. Free, and the free account is all this course ever needs.",
                note: "Your username becomes part of your app's web address, so pick one you would not mind a customer reading.",
            },
            {
                id: "repo",
                icon: "📦",
                title: "Make an EMPTY repository",
                body: "Click **New**, name it after your app, and choose **Public**. Then the part that matters: **do not tick \"Add a README file\"**, and leave the .gitignore and licence dropdowns alone.",
                note: "A repo created with a README already has a version in it, and pushing your folder into it fails with an error about *non-fast-forward* that never says \"your repository is not empty\". Public is not optional either — free GitHub Pages only publishes public repos, and that is two lessons from now.",
            },
            {
                id: "upload",
                icon: "⬆️",
                title: "Get your files up there",
                body: {
                    claudecode: "Ask it, in your own words: **put this project on GitHub, in my repo at github.com/USERNAME/REPO-NAME — make the first commit say \"First version of my app\"**. If it asks you to sign in to GitHub, do it; that happens once.",
                    browser: "On your empty repo's page, click **uploading an existing file**. Open your app folder, select all the files inside it — the files, not the folder — and drag them onto the page. Write `First version of my app` in the **Commit changes** box and click **Commit changes**.",
                },
                note: {
                    claudecode: "It runs `git init`, `git add`, `git commit` and `git push` — the four commands from the last lesson. Watch them go past; you are meant to recognise them, not memorise them.",
                    browser: "Dragging the folder instead of the files is the one wrong turn here. You want `index.html` at the top of the repo, not one level down.",
                },
            },
            {
                id: "verify",
                icon: "✅",
                title: "Look at it",
                body: "Refresh your repo page. Your files are listed, `index.html` among them, and it says **1 commit** near the top. That is your app online, with a history.",
                note: "It is not published yet — nobody can use it. That is the next two lessons. But it is safe now, and that is the part you cannot get back later.",
            },
        ],
        exercise: "Put your app on GitHub, using the checklist beside this lesson. Choose at the top whether Claude Code is running the commands or you are doing it through github.com — both paths finish in the same place — and tick each step as you go.",
        hint: "If the push fails and the error mentions *non-fast-forward* or *rejected*, your repository was not empty. The quickest fix is to delete it and make a new one with nothing ticked. If you are stuck on anything else, copy the error and paste it to Claude below.",
    },

    es: {
        title: "Tu lista de GitHub",
        allDone: "Listo: tu código ya está en GitHub.",
        intro: "Ve marcando cada paso conforme lo haces de verdad. Tu progreso se guarda, así que puedes salir, hacer el paso y volver a esta página.",
        selfAttested: "Aquí no se revisa nada automáticamente: esta lista es tuya, para llevar la cuenta, y la marcas con honestidad.",
        osLabel: "¿Cómo lo vas a hacer?",
        alreadyDone: "Mi app ya está en GitHub",
        alreadyDoneHint: "Marca todo. Úsalo solo si tus archivos de verdad se ven en github.com/tu-usuario/tu-repo.",
        gateNote: "Resuelve primero lo de la carpeta. Si esa está mal, todos los pasos siguientes están mal.",
        os: [
            { id: "claudecode", label: "Claude Code lo hace por mí", icon: "🤖" },
            { id: "browser", label: "Lo hago en github.com", icon: "🌐" },
        ],
        steps: [
            {
                id: "folder",
                icon: "📁",
                gate: true,
                title: "Ten claro qué carpeta vas a subir",
                body: {
                    claudecode: "Es la carpeta que Claude Code ya tiene abierta, la del módulo anterior. Revisa que tenga un `index.html` directamente adentro, no metido en otra carpeta.",
                    browser: "Es la carpeta de tu app ya descomprimida, la del botón **Descargar mi app**. Ábrela y revisa que tenga un `index.html` directamente adentro, no metido en otra carpeta.",
                },
                note: "¿No la tienes o no la encuentras? Con una carpeta vacía y un solo `index.html` adentro alcanza para todo lo de abajo, y Claude te escribe uno si se lo pides. No pierdas veinte minutos buscando.",
            },
            {
                id: "account",
                icon: "🐙",
                title: "Crea una cuenta de GitHub",
                body: "Entra a **github.com** y regístrate. Es gratis, y la cuenta gratuita es todo lo que este curso necesita.",
                note: "Tu nombre de usuario va a ser parte de la dirección web de tu app, así que elige uno que no te importe que lea un cliente.",
            },
            {
                id: "repo",
                icon: "📦",
                title: "Crea un repositorio VACÍO",
                body: "Haz clic en **New**, ponle el nombre de tu app y elige **Public**. Y luego lo que de verdad importa: **no marques «Add a README file»**, y no toques los menús de .gitignore ni de licencia.",
                note: "Un repo creado con README ya tiene una versión adentro, y al subir tu carpeta falla con un error de *non-fast-forward* que nunca dice «tu repositorio no está vacío». Public tampoco es opcional: GitHub Pages gratis solo publica repos públicos, y eso es dentro de dos lecciones.",
            },
            {
                id: "upload",
                icon: "⬆️",
                title: "Sube tus archivos",
                body: {
                    claudecode: "Pídeselo con tus palabras: **sube este proyecto a GitHub, a mi repo en github.com/USUARIO/NOMBRE-DEL-REPO — que el primer commit diga \"Primera versión de mi app\"**. Si te pide iniciar sesión en GitHub, hazlo; eso pasa una sola vez.",
                    browser: "En la página de tu repo vacío, haz clic en **uploading an existing file**. Abre la carpeta de tu app, selecciona todos los archivos que están adentro — los archivos, no la carpeta — y arrástralos a la página. Escribe `Primera versión de mi app` en la caja **Commit changes** y haz clic en **Commit changes**.",
                },
                note: {
                    claudecode: "Ejecuta `git init`, `git add`, `git commit` y `git push`: los cuatro comandos de la lección pasada. Míralos pasar; la idea es que los reconozcas, no que te los aprendas.",
                    browser: "Arrastrar la carpeta en vez de los archivos es la única vuelta equivocada aquí. Lo que quieres es que `index.html` quede hasta arriba del repo, no un nivel más abajo.",
                },
            },
            {
                id: "verify",
                icon: "✅",
                title: "Míralo",
                body: "Recarga la página de tu repo. Ahí están tus archivos, con `index.html` entre ellos, y arriba dice **1 commit**. Esa es tu app en línea, con historial.",
                note: "Todavía no está publicada: nadie la puede usar. De eso tratan las dos lecciones siguientes. Pero ya está a salvo, y eso es lo que no se recupera después.",
            },
        ],
        exercise: "Sube tu app a GitHub usando la lista que está junto a esta lección. Elige arriba si Claude Code ejecuta los comandos o si lo haces desde github.com —ambos caminos terminan en el mismo lugar— y ve marcando cada paso conforme avanzas.",
        hint: "Si la subida falla y el error menciona *non-fast-forward* o *rejected*, tu repositorio no estaba vacío. Lo más rápido es borrarlo y crear otro sin marcar nada. Si te atoras en otra cosa, copia el error y pégaselo a Claude aquí abajo.",
    },
};

const PUBLISH_WITH_PAGES = {
    en: {
        title: "Your publishing checklist",
        allDone: "All done — your app is live, and you sent someone the link.",
        intro: "Tick each one as you actually do it. Your progress is saved, so you can leave, do the step, and come back to this page.",
        selfAttested: "Nothing here is checked automatically — this list is yours to keep track with, and you tick it honestly.",
        osLabel: "How are you doing this?",
        alreadyDone: "My app is already live at its own address",
        alreadyDoneHint: "Ticks everything. Only use it if you have opened your app from its github.io address in a browser.",
        gateNote: "Check this first. On a private repo the publishing option is greyed out, and the explanation is easy to misread as a bug.",
        os: [
            { id: "claudecode", label: "Claude Code does it for me", icon: "🤖" },
            { id: "browser", label: "I'll use github.com", icon: "🌐" },
        ],
        steps: [
            {
                id: "public",
                icon: "🔓",
                gate: true,
                title: "Check your repo is public",
                body: "Open your repo and look next to its name at the top: it says **Public** or **Private**. Free GitHub Pages only publishes public repos.\n\nIf it says Private: **Settings** → **General** → scroll to the bottom → **Change repository visibility**. It lives in a red box called the Danger Zone, which is alarming and, for this, entirely routine.",
                note: "Public means anyone can read your code. That is normal. It also means never leaving a password, an API key or a customer's details in these files — not in `config.js`, not in a comment. If any are in there, take them out before you continue.",
            },
            {
                id: "pages",
                icon: "🚀",
                title: "Turn Pages on",
                body: "**Settings** → **Pages** in the left sidebar. Under **Source**, choose **Deploy from a branch**. Two dropdowns appear: pick branch **main** and folder **/ (root)**. Click **Save**.",
                note: "Root, because your `index.html` is at the top of the repo. If your files landed inside a subfolder instead, Pages serves nothing and returns a 404 — move them up to the top and try again.",
            },
            {
                id: "wait",
                icon: "⏳",
                title: "Wait for the address, then wait a bit longer",
                body: "Refresh the Pages screen and your address appears at the top: `https://USERNAME.github.io/REPO-NAME/`",
                note: "The first build takes about a minute, so a 404 in that first minute is normal rather than a mistake. This is where most people give up thirty seconds early.",
            },
            {
                id: "open",
                icon: "🌍",
                title: "Open it",
                body: "Click the link. Your app loads from the internet — not from a file on your computer, not from inside this course.",
                note: "Your app saves data in whichever browser opens it, so what you add on your laptop will not appear on your phone. Connecting Firebase is what fixes that, and `CONNECT-FIREBASE.md` in your folder is the instructions — it needs a real address like this one, which you now have.",
            },
            {
                id: "phone",
                icon: "📱",
                title: "Open it on your phone",
                body: "Type the address into your phone's browser, or send yourself the link. Read it the way a stranger would.",
                note: {
                    claudecode: "Anything too small, cut off at the edge, or needing a pinch to read is a real problem with a real fix. Describe it to Claude Code, let it change the file, and you will push the fix in the next lesson.",
                    browser: "Anything too small, cut off at the edge, or needing a pinch to read is a real problem with a real fix. Screenshot it, paste it to Claude below, and ask what to change.",
                },
            },
            {
                id: "send",
                icon: "💌",
                title: "Send it to one person",
                body: "Not \"share it later\" — now, to one actual human. A friend, someone in your family, a customer.",
                note: "You wrote a spec on day one, built an app, and put it on the internet at an address you own. Sending it is what turns that from a course exercise into a thing that exists.",
            },
        ],
        exercise: "Publish your app with GitHub Pages, using the checklist beside this lesson, and finish by opening it on your phone and sending the link to one person.",
        hint: "A 404 that lasts more than a couple of minutes is almost always one of two things: the repo is private, or `index.html` is inside a subfolder instead of at the top. Check those two before anything else.",
    },

    es: {
        title: "Tu lista para publicar",
        allDone: "Listo: tu app está en línea y ya le mandaste el link a alguien.",
        intro: "Ve marcando cada paso conforme lo haces de verdad. Tu progreso se guarda, así que puedes salir, hacer el paso y volver a esta página.",
        selfAttested: "Aquí no se revisa nada automáticamente: esta lista es tuya, para llevar la cuenta, y la marcas con honestidad.",
        osLabel: "¿Cómo lo vas a hacer?",
        alreadyDone: "Mi app ya está en línea con su propia dirección",
        alreadyDoneHint: "Marca todo. Úsalo solo si ya abriste tu app desde su dirección github.io en un navegador.",
        gateNote: "Revisa esto primero. En un repo privado la opción de publicar sale en gris, y la explicación es fácil de confundir con un error.",
        os: [
            { id: "claudecode", label: "Claude Code lo hace por mí", icon: "🤖" },
            { id: "browser", label: "Lo hago en github.com", icon: "🌐" },
        ],
        steps: [
            {
                id: "public",
                icon: "🔓",
                gate: true,
                title: "Revisa que tu repo sea público",
                body: "Abre tu repo y mira junto al nombre, hasta arriba: dice **Public** o **Private**. GitHub Pages gratis solo publica repos públicos.\n\nSi dice Private: **Settings** → **General** → hasta abajo → **Change repository visibility**. Está en un recuadro rojo que se llama Danger Zone, que asusta y que, para esto, es de lo más normal.",
                note: "Público significa que cualquiera puede leer tu código. Eso es normal. También significa no dejar nunca una contraseña, una API key ni los datos de un cliente en estos archivos: ni en `config.js`, ni en un comentario. Si hay alguno, sácalo antes de seguir.",
            },
            {
                id: "pages",
                icon: "🚀",
                title: "Enciende Pages",
                body: "**Settings** → **Pages** en la columna de la izquierda. En **Source**, elige **Deploy from a branch**. Aparecen dos menús: escoge la rama **main** y la carpeta **/ (root)**. Haz clic en **Save**.",
                note: "Root, porque tu `index.html` está hasta arriba del repo. Si tus archivos quedaron dentro de una subcarpeta, Pages no sirve nada y devuelve un 404: muévelos hasta arriba y vuelve a intentar.",
            },
            {
                id: "wait",
                icon: "⏳",
                title: "Espera la dirección, y luego espera tantito más",
                body: "Recarga la pantalla de Pages y tu dirección aparece hasta arriba: `https://USUARIO.github.io/NOMBRE-DEL-REPO/`",
                note: "La primera construcción tarda como un minuto, así que un 404 en ese primer minuto es normal, no es un error tuyo. Aquí es donde la mayoría se rinde treinta segundos antes de tiempo.",
            },
            {
                id: "open",
                icon: "🌍",
                title: "Ábrela",
                body: "Haz clic en el link. Tu app carga desde internet: no desde un archivo de tu computadora, no desde dentro de este curso.",
                note: "Tu app guarda los datos en el navegador donde se abre, así que lo que agregues en la laptop no va a aparecer en el teléfono. Eso lo arregla conectar Firebase, y `CONNECT-FIREBASE.md` en tu carpeta son las instrucciones — necesita una dirección real como esta, que ya tienes.",
            },
            {
                id: "phone",
                icon: "📱",
                title: "Ábrela en tu teléfono",
                body: "Escribe la dirección en el navegador de tu teléfono, o mándate el link. Léela como la leería un desconocido.",
                note: {
                    claudecode: "Todo lo que se vea muy chico, se corte en la orilla o tengas que agrandar con los dedos es un problema real con una solución real. Descríbeselo a Claude Code, deja que cambie el archivo, y subes el arreglo en la siguiente lección.",
                    browser: "Todo lo que se vea muy chico, se corte en la orilla o tengas que agrandar con los dedos es un problema real con una solución real. Tómale captura, pégasela a Claude aquí abajo y pregunta qué cambiar.",
                },
            },
            {
                id: "send",
                icon: "💌",
                title: "Mándasela a una persona",
                body: "No «la comparto después»: ahora, a un ser humano de verdad. Un amigo, alguien de tu familia, un cliente.",
                note: "Escribiste una especificación el primer día, construiste una app y la pusiste en internet en una dirección que es tuya. Mandarla es lo que hace que deje de ser un ejercicio del curso y se convierta en algo que existe.",
            },
        ],
        exercise: "Publica tu app con GitHub Pages usando la lista que está junto a esta lección, y termina abriéndola en tu teléfono y mandándole el link a una persona.",
        hint: "Un 404 que dura más de un par de minutos casi siempre es una de dos cosas: el repo es privado, o `index.html` está dentro de una subcarpeta en vez de hasta arriba. Revisa esas dos antes que nada.",
    },
};

const SHIP_A_CHANGE = {
    en: {
        title: "Your shipping loop",
        allDone: "All done — you changed a live app and put it back the way it was.",
        intro: "Tick each one as you actually do it. Your progress is saved, so you can leave, do the step, and come back to this page.",
        selfAttested: "Nothing here is checked automatically — this list is yours to keep track with, and you tick it honestly.",
        osLabel: "How are you doing this?",
        alreadyDone: "I have already shipped a change and undone one",
        alreadyDoneHint: "Ticks everything. Only use it if you have watched a change of yours appear on the live site.",
        gateNote: "Pick the change first, and keep it small. A big change hides which step went wrong.",
        os: [
            { id: "claudecode", label: "Claude Code does it for me", icon: "🤖" },
            { id: "browser", label: "I'll use github.com", icon: "🌐" },
        ],
        steps: [
            {
                id: "pick",
                icon: "🎯",
                gate: true,
                title: "Pick one small visible thing",
                body: "A word, a colour, a button's label — not a feature. Good ones: the heading on the front page, your app's colour, the text on the main button.",
                note: "The point of this lesson is the loop, not the change. Small and visible means you can tell instantly whether it worked.",
            },
            {
                id: "change",
                icon: "✏️",
                title: "Change it",
                body: {
                    claudecode: "Ask for it in your own words: **change the heading on the front page to \"Open every day until 9\"**.",
                    browser: "On your repo, click the file, then the pencil icon at the top right. Edit the text directly on the page.",
                },
            },
            {
                id: "save",
                icon: "💾",
                title: "Save the version, with a message that means something",
                body: {
                    claudecode: "Ask for the commit and the push together: **commit that with a message saying what changed, and push it**.",
                    browser: "The **Commit changes** box is the commit and the push in one. Write `Changed the front page heading` and confirm.",
                },
                note: "One sentence about what changed. Last lesson you saw how those lines read in public — this is you writing one.",
            },
            {
                id: "live",
                icon: "🌍",
                title: "Watch it go live",
                body: "Go back to your address and refresh. Give it a minute — Pages rebuilds on every push, same as the first time.",
                note: "That is the loop, finished. It works the same at ten changes a day as it does at one a month.",
            },
            {
                id: "undo",
                icon: "↩️",
                title: "Now practise going back",
                body: {
                    claudecode: "Break something on purpose — delete a chunk of a file, commit it, push it, watch the live site get worse. Then ask: **go back to the version before that last change**.",
                    browser: "Break something on purpose — delete a chunk of a file in the web editor, commit it, watch the live site get worse. Then open that file's **History**, find the commit before the bad one, and use **Revert**.",
                },
                note: "This is the step people skip and then need on their worst day. The live site heals within the minute, and nothing is lost — because you saved a version before you started.",
            },
        ],
        exercise: "Ship one small change to your live app, using the checklist beside this lesson, then break something on purpose and put it back. Both halves matter — the second one is the reason the first is safe.",
        hint: "If the live site still shows the old version after a couple of minutes, refresh with the cache cleared (Ctrl+Shift+R, or Cmd+Shift+R on a Mac). Browsers hold on to the old files longer than GitHub does.",
    },

    es: {
        title: "Tu ciclo de publicación",
        allDone: "Listo: cambiaste una app en vivo y la dejaste como estaba.",
        intro: "Ve marcando cada paso conforme lo haces de verdad. Tu progreso se guarda, así que puedes salir, hacer el paso y volver a esta página.",
        selfAttested: "Aquí no se revisa nada automáticamente: esta lista es tuya, para llevar la cuenta, y la marcas con honestidad.",
        osLabel: "¿Cómo lo vas a hacer?",
        alreadyDone: "Ya publiqué un cambio y ya deshice uno",
        alreadyDoneHint: "Marca todo. Úsalo solo si ya viste un cambio tuyo aparecer en el sitio en vivo.",
        gateNote: "Elige el cambio primero, y que sea chico. Un cambio grande esconde cuál paso salió mal.",
        os: [
            { id: "claudecode", label: "Claude Code lo hace por mí", icon: "🤖" },
            { id: "browser", label: "Lo hago en github.com", icon: "🌐" },
        ],
        steps: [
            {
                id: "pick",
                icon: "🎯",
                gate: true,
                title: "Elige una cosa chica y visible",
                body: "Una palabra, un color, el texto de un botón — no una función. Buenas opciones: el título de la portada, el color de tu app, el texto del botón principal.",
                note: "Lo que enseña esta lección es el ciclo, no el cambio. Chico y visible significa que te das cuenta al instante de si funcionó.",
            },
            {
                id: "change",
                icon: "✏️",
                title: "Cámbialo",
                body: {
                    claudecode: "Pídelo con tus palabras: **cambia el título de la portada a \"Abierto todos los días hasta las 9\"**.",
                    browser: "En tu repo, haz clic en el archivo y luego en el ícono del lápiz arriba a la derecha. Edita el texto ahí mismo en la página.",
                },
            },
            {
                id: "save",
                icon: "💾",
                title: "Guarda la versión, con un mensaje que diga algo",
                body: {
                    claudecode: "Pide el commit y la subida juntos: **haz commit de eso con un mensaje que diga qué cambió, y súbelo**.",
                    browser: "La caja **Commit changes** es el commit y la subida en un solo paso. Escribe `Cambié el título de la portada` y confirma.",
                },
                note: "Una frase sobre qué cambió. La lección pasada viste cómo se leen esos renglones en público; esta vez lo escribes tú.",
            },
            {
                id: "live",
                icon: "🌍",
                title: "Míralo salir en vivo",
                body: "Vuelve a tu dirección y recarga. Dale un minuto: Pages reconstruye en cada subida, igual que la primera vez.",
                note: "Ese es el ciclo, completo. Funciona igual con diez cambios al día que con uno al mes.",
            },
            {
                id: "undo",
                icon: "↩️",
                title: "Ahora practica volver atrás",
                body: {
                    claudecode: "Rompe algo a propósito: borra un pedazo de un archivo, haz commit, súbelo y ve cómo empeora el sitio en vivo. Luego pide: **vuelve a la versión anterior a ese último cambio**.",
                    browser: "Rompe algo a propósito: borra un pedazo de un archivo en el editor web, haz commit y ve cómo empeora el sitio en vivo. Luego abre el **History** de ese archivo, busca el commit anterior al malo y usa **Revert**.",
                },
                note: "Este es el paso que la gente se salta y que luego necesita justo en su peor día. El sitio en vivo se cura en menos de un minuto y no se pierde nada, porque guardaste una versión antes de empezar.",
            },
        ],
        exercise: "Publica un cambio chico en tu app en vivo usando la lista que está junto a esta lección, y después rompe algo a propósito y déjalo como estaba. Las dos mitades importan: la segunda es la razón por la que la primera es segura.",
        hint: "Si después de un par de minutos el sitio en vivo sigue mostrando la versión vieja, recarga limpiando la caché (Ctrl+Shift+R, o Cmd+Shift+R en Mac). Los navegadores se quedan con los archivos viejos más tiempo que GitHub.",
    },
};

/* ─────────────────────────────────────────────────────────────────────────────
 * CD04's Module 5, shipping with Firebase Hosting — V4512, V4513, V4514, V4515.
 *
 * These ran as an alternative "Module 5.5" beside a GitHub Pages module until 2026-08-10,
 * when Firebase was chosen and the GitHub one was retired from CD04. GITHUB_PUSH,
 * PUBLISH_WITH_PAGES and SHIP_A_CHANGE above now serve CD02 only.
 *
 * THE BRANCH IS NOT `browser` HERE, and that is the trade CD04 accepted rather than a
 * detail. The Firebase console has no upload-a-folder button, so `firebase deploy` is the
 * only way in and a terminal is a hard floor. These branch `claudecode` / `terminal`:
 * either Claude Code runs the commands, or the student types them. CD02's GitHub module
 * could finish with no install at all; here a student with no paid Claude plan types four
 * commands. Nothing in the copy pretends otherwise, and it is the first thing to revisit
 * if this module's completion rate disappoints.
 *
 * V4512 is the exception and branches on something else entirely — `existing` / `new`,
 * meaning "do you already have a Firebase project?" — because a student who connected
 * `config.js` to Firestore already has one, and making a SECOND project here is the
 * quiet disaster of this module: the app deploys to one project and its data lives in
 * another, everything works locally, and the live site is empty with no error anywhere.
 *
 * KEEP IN STEP WITH THE PROSE, same rule as the GitHub four: every step below is one
 * `###` heading of the matching lesson in backend/src/data/authoredLessonProse.js.
 *
 * Verified against console.firebase.google.com and firebase-tools 2026-08-09. Facts
 * these four commit to:
 *   - Firebase Hosting is free on the Spark plan and asks for NO payment method.
 *   - App Hosting is a DIFFERENT product, sits beside Hosting in the sidebar, and
 *     requires the paid Blaze plan. Picking it is how a student hits a paywall.
 *   - `firebase deploy --project <id>` needs only `firebase.json` — which the project
 *     template already ships, with `public: "."` — and never writes `.firebaserc`.
 *   - `firebase init hosting` OFFERS TO OVERWRITE index.html. A student who accepts has
 *     replaced their app with Firebase's welcome page. These lessons never run it.
 *   - The live address is https://PROJECT-ID.web.app (and .firebaseapp.com).
 *   - The Project ID is not always the name typed in: the console appends a suffix when
 *     the name is taken, and the ID is what every command needs.
 *   - Hosting keeps a release history, and any past release can be rolled back from the
 *     console. This is the one thing this track does that GitHub Pages cannot.
 * ───────────────────────────────────────────────────────────────────────────── */

const FIREBASE_PROJECT = {
    en: {
        title: "Your Firebase project checklist",
        allDone: "All done — you have a Firebase project and you know its ID.",
        intro: "Tick each one as you actually do it. Your progress is saved, so you can leave, do the step, and come back to this page.",
        selfAttested: "Nothing here is checked automatically — this list is yours to keep track with, and you tick it honestly.",
        osLabel: "Do you already have a Firebase project?",
        alreadyDone: "I have my project and I know its Project ID",
        alreadyDoneHint: "Ticks everything. Only use it if you can actually read the Project ID on the console's settings page.",
        gateNote: "Read this one before you click anything. The console shows upgrade prompts, and knowing which to ignore is the difference between free and not.",
        os: [
            { id: "new", label: "No, I'm making one now", icon: "✨" },
            { id: "existing", label: "Yes, I connected one already", icon: "🔌" },
        ],
        steps: [
            {
                id: "free",
                icon: "🆓",
                gate: true,
                title: "Know that this is free, and know what to avoid",
                body: "Firebase's free tier is called the **Spark** plan. Hosting your app on it costs nothing and **never asks for a card**. You are not starting a trial and there is nothing to cancel.",
                note: "There is exactly one way to end up somewhere that wants a card, and it is worth knowing now: in the sidebar, **App Hosting** is a different product from **Hosting**, and it requires the paid Blaze plan. This course uses **Hosting** — the one without the word App. If a screen ever asks for a payment method, you clicked the wrong one; go back.",
            },
            {
                id: "console",
                icon: "🌐",
                title: "Open the Firebase console",
                body: {
                    new: "Go to **console.firebase.google.com** and sign in with your Google account. Any Gmail works, and you do not need a new one.",
                    existing: "Go to **console.firebase.google.com** and sign in with the Google account you used when you filled in `config.js`. If you have more than one, this is the step to get right.",
                },
                note: {
                    new: "This is the same Google account you will sign the deploy tool into two lessons from now. Note which one you used.",
                    existing: "Your project should be listed on this screen. If it is not, you are signed in as somebody else — sign out and come back with the right account.",
                },
            },
            {
                id: "create",
                icon: "➕",
                os: ["new"],
                title: "Create the project",
                body: "Click **Create a project**, and name it after your app. The name is for you; the address comes from it but does not have to be pretty yet.",
                note: "If a screen offers to enable extra products, you can pass on all of them. Nothing this course does needs anything beyond Hosting, and Firestore if you connect your data later.",
            },
            {
                id: "analytics",
                icon: "⏭️",
                os: ["new"],
                title: "Skip Google Analytics",
                body: "One of the setup screens offers **Google Analytics**. Turn it off and continue.",
                note: "It is not a trap and it costs nothing — it is just a second product with its own account-linking screens, and it has nothing to do with putting your app online. You can add it later if you ever want it.",
            },
            {
                id: "projectid",
                icon: "🔑",
                title: "Find your Project ID and write it down",
                body: "Open **⚙️ Project settings** from the sidebar. Near the top is **Project ID**. Copy it somewhere you can find in five minutes — a note, a sticky, anywhere.",
                note: "The Project ID is NOT always what you typed. If your name was taken the console adds a suffix, so `taco-tracker` can become `taco-tracker-4f21`. Every command in the next two lessons uses the ID, not the name, and that mismatch is the single most common way this track goes wrong.",
            },
        ],
        exercise: "Make your Firebase project — or open the one you already made — and get its Project ID written down somewhere you can find again. Use the checklist beside this lesson, and choose at the top whether you are starting fresh or already have one.",
        hint: "If you already connected Firebase to your app in `config.js`, choose \"Yes\" at the top and reuse that project. Do not make a second one: your app would be published under one project while its data lives in another, and nothing would show an error — the live site would just come up empty.",
    },

    es: {
        title: "Tu lista del proyecto de Firebase",
        allDone: "Listo: ya tienes tu proyecto de Firebase y sabes su Project ID.",
        intro: "Ve marcando cada paso conforme lo haces de verdad. Tu progreso se guarda, así que puedes salir, hacer el paso y volver a esta página.",
        selfAttested: "Aquí no se revisa nada automáticamente: esta lista es tuya, para llevar la cuenta, y la marcas con honestidad.",
        osLabel: "¿Ya tienes un proyecto de Firebase?",
        alreadyDone: "Ya tengo mi proyecto y sé su Project ID",
        alreadyDoneHint: "Marca todo. Úsalo solo si de verdad puedes leer el Project ID en la página de configuración de la consola.",
        gateNote: "Lee este antes de hacer clic en nada. La consola te va a ofrecer mejoras de plan, y saber cuáles ignorar es la diferencia entre gratis y no.",
        os: [
            { id: "new", label: "No, lo voy a crear ahora", icon: "✨" },
            { id: "existing", label: "Sí, ya conecté uno", icon: "🔌" },
        ],
        steps: [
            {
                id: "free",
                icon: "🆓",
                gate: true,
                title: "Ten claro que esto es gratis, y qué debes evitar",
                body: "El plan gratuito de Firebase se llama **Spark**. Publicar tu app ahí no cuesta nada y **nunca te pide tarjeta**. No estás empezando ninguna prueba y no hay nada que cancelar.",
                note: "Hay exactamente una forma de terminar en una pantalla que te pida tarjeta, y vale la pena saberla desde ahora: en el menú lateral, **App Hosting** es un producto distinto de **Hosting**, y ese sí exige el plan de pago Blaze. Este curso usa **Hosting**, el que no dice App. Si alguna pantalla te pide forma de pago, le diste al equivocado; regrésate.",
            },
            {
                id: "console",
                icon: "🌐",
                title: "Abre la consola de Firebase",
                body: {
                    new: "Entra a **console.firebase.google.com** e inicia sesión con tu cuenta de Google. Cualquier Gmail sirve, y no necesitas crear una nueva.",
                    existing: "Entra a **console.firebase.google.com** e inicia sesión con la cuenta de Google que usaste cuando llenaste `config.js`. Si tienes más de una, este es el paso que hay que atinarle.",
                },
                note: {
                    new: "Esta es la misma cuenta de Google con la que vas a entrar en la herramienta de publicación dentro de dos lecciones. Apunta cuál usaste.",
                    existing: "Tu proyecto debería aparecer en esta pantalla. Si no está, entraste con otra cuenta: cierra sesión y vuelve con la correcta.",
                },
            },
            {
                id: "create",
                icon: "➕",
                os: ["new"],
                title: "Crea el proyecto",
                body: "Haz clic en **Create a project** y ponle el nombre de tu app. El nombre es para ti; la dirección sale de ahí, pero todavía no tiene que quedar bonita.",
                note: "Si una pantalla te ofrece activar productos extra, puedes decir que no a todos. Nada de lo que hace este curso necesita algo más que Hosting, y Firestore si más adelante conectas tus datos.",
            },
            {
                id: "analytics",
                icon: "⏭️",
                os: ["new"],
                title: "Sáltate Google Analytics",
                body: "Una de las pantallas de configuración te ofrece **Google Analytics**. Desactívalo y sigue adelante.",
                note: "No es una trampa ni cuesta nada: simplemente es otro producto, con sus propias pantallas para enlazar cuentas, y no tiene nada que ver con poner tu app en internet. Lo puedes agregar después si algún día lo quieres.",
            },
            {
                id: "projectid",
                icon: "🔑",
                title: "Encuentra tu Project ID y anótalo",
                body: "Abre **⚙️ Project settings** en el menú lateral. Arriba está el **Project ID**. Cópialo en algún lugar donde lo vuelvas a encontrar en cinco minutos: una nota, un papelito, donde sea.",
                note: "El Project ID NO siempre es lo que escribiste. Si tu nombre ya estaba ocupado, la consola le agrega un sufijo, así que `taco-tracker` puede quedar como `taco-tracker-4f21`. Todos los comandos de las dos lecciones siguientes usan el ID, no el nombre, y esa confusión es la forma más común de que este camino salga mal.",
            },
        ],
        exercise: "Crea tu proyecto de Firebase —o abre el que ya habías creado— y deja anotado su Project ID en algún lugar donde lo vuelvas a encontrar. Usa la lista que está junto a esta lección y elige arriba si empiezas de cero o si ya tenías uno.",
        hint: "Si ya conectaste Firebase a tu app en `config.js`, elige «Sí» arriba y reutiliza ese proyecto. No crees uno segundo: tu app quedaría publicada en un proyecto y sus datos en otro, y no saldría ningún error — el sitio en vivo simplemente se vería vacío.",
    },
};

const INSTALL_FIREBASE_CLI = {
    en: {
        title: "Your setup checklist",
        allDone: "All done — the deploy tool is installed and signed in.",
        intro: "Tick each one as you actually do it. Your progress is saved, so you can leave, do the step, and come back to this page.",
        selfAttested: "Nothing here is checked automatically — this list is yours to keep track with, and you tick it honestly.",
        osLabel: "How are you doing this?",
        alreadyDone: "The tool is installed and I'm signed in",
        alreadyDoneHint: "Ticks everything. Only use it if `firebase projects:list` actually printed your project.",
        gateNote: "Check this first. Half the people doing this already have Node and do not need the step after it.",
        os: [
            { id: "claudecode", label: "Claude Code does it for me", icon: "🤖" },
            { id: "terminal", label: "I'll type the commands", icon: "⌨️" },
        ],
        steps: [
            {
                id: "node",
                icon: "🔍",
                gate: true,
                title: "Check whether you already have Node",
                body: {
                    claudecode: "Ask Claude Code, in your own words: **do I have Node installed? check the version**. It will run the check and tell you.",
                    terminal: "Open your terminal — Terminal on a Mac, PowerShell on Windows — and type `node --version`, then Enter.",
                },
                note: {
                    claudecode: "If it says you do not have it, that is fine and it is the next step. Nothing is broken.",
                    terminal: "A version number like `v20.11.0` means you have it — skip the next step. `command not found` or `not recognized` means you do not, which is normal and is the next step.",
                },
            },
            {
                id: "install-node",
                icon: "⬇️",
                title: "Install Node, if you need it",
                body: {
                    claudecode: "Ask: **install Node for me**. It will tell you what it is about to do — read it, then let it. If it asks for your password, that is the installer needing permission, and it is expected.",
                    terminal: "Go to **nodejs.org** and download the version marked **LTS**. Run the installer and accept the defaults all the way through. Then close your terminal completely and open a new one.",
                },
                note: {
                    claudecode: "Skip this if the check above already gave you a version number.",
                    terminal: "Closing and reopening the terminal is not optional — a terminal that was already open cannot see a program installed after it started, and this is the number one reason the next step says \"command not found\" on a machine that just installed Node fine.",
                },
            },
            {
                id: "install-cli",
                icon: "🧰",
                title: "Install the Firebase tool",
                body: {
                    claudecode: "Ask: **install the Firebase CLI**. It runs `npm install -g firebase-tools`, which is one command that downloads the tool that publishes your app.",
                    terminal: "In your terminal, type this and press Enter:\n\n```\nnpm install -g firebase-tools\n```\n\nIt prints a lot of lines and takes a minute or two. That is normal.",
                },
                note: {
                    claudecode: "If it fails on permissions, say so and let it read the error — this is exactly the kind of machine-specific problem it is good at, and guessing at it yourself is how an afternoon disappears.",
                    terminal: "On Windows, if you get a message about scripts being disabled, copy the whole error and paste it to Claude below. On a Mac, if it mentions `EACCES` or permission denied, do the same. Both are normal, both are fixable, and neither is worth guessing at.",
                },
            },
            {
                id: "login",
                icon: "🔐",
                title: "Sign in",
                body: {
                    claudecode: "Ask: **run firebase login**. A browser window opens; choose your Google account and approve it.",
                    terminal: "Type `firebase login` and press Enter. A browser window opens; choose your Google account and approve it.",
                },
                note: "Use **the same Google account** you made the project with in the last lesson. Signing in with a different one is not an error and looks completely fine — you just will not be able to see your project, and the next step is where you find that out.",
            },
            {
                id: "verify",
                icon: "✅",
                title: "Prove it worked",
                body: {
                    claudecode: "Ask: **list my Firebase projects**. Your project's name and its Project ID come back in a little table.",
                    terminal: "Type `firebase projects:list` and press Enter. Your project's name and its Project ID come back in a little table.",
                },
                note: "Check the Project ID in that table against the one you wrote down last lesson. They must match. If your project is not listed at all, you signed in with the wrong Google account — run `firebase logout`, then sign in again.",
            },
        ],
        exercise: "Get the Firebase tool installed and signed in, using the checklist beside this lesson. Choose at the top whether Claude Code is running the commands or you are typing them. You are done when a list of your Firebase projects prints in the terminal with your Project ID in it.",
        hint: "Almost everything that goes wrong here is one of three things: a terminal you did not restart after installing Node, a permissions error on the install, or signing in with the wrong Google account. Copy the exact error and paste it to Claude below — it fixes these quickly, and none of them are worth an afternoon.",
    },

    es: {
        title: "Tu lista de instalación",
        allDone: "Listo: la herramienta está instalada y con tu sesión iniciada.",
        intro: "Ve marcando cada paso conforme lo haces de verdad. Tu progreso se guarda, así que puedes salir, hacer el paso y volver a esta página.",
        selfAttested: "Aquí no se revisa nada automáticamente: esta lista es tuya, para llevar la cuenta, y la marcas con honestidad.",
        osLabel: "¿Cómo lo vas a hacer?",
        alreadyDone: "Ya está instalada y ya inicié sesión",
        alreadyDoneHint: "Marca todo. Úsalo solo si `firebase projects:list` de verdad te imprimió tu proyecto.",
        gateNote: "Revisa esto primero. La mitad de la gente ya tiene Node y no necesita el paso que sigue.",
        os: [
            { id: "claudecode", label: "Claude Code lo hace por mí", icon: "🤖" },
            { id: "terminal", label: "Yo escribo los comandos", icon: "⌨️" },
        ],
        steps: [
            {
                id: "node",
                icon: "🔍",
                gate: true,
                title: "Revisa si ya tienes Node",
                body: {
                    claudecode: "Pídeselo a Claude Code con tus palabras: **¿tengo Node instalado? revisa la versión**. Él hace la revisión y te dice.",
                    terminal: "Abre tu terminal —Terminal en Mac, PowerShell en Windows— y escribe `node --version`, luego Enter.",
                },
                note: {
                    claudecode: "Si te dice que no lo tienes, no pasa nada: ese es justo el paso siguiente. No se rompió nada.",
                    terminal: "Un número de versión como `v20.11.0` significa que sí lo tienes: sáltate el paso siguiente. `command not found` o `no se reconoce` significa que no, lo cual es normal y es el paso siguiente.",
                },
            },
            {
                id: "install-node",
                icon: "⬇️",
                title: "Instala Node, si te hace falta",
                body: {
                    claudecode: "Pídele: **instálame Node**. Te va a decir qué está por hacer: léelo y déjalo. Si te pide tu contraseña, es el instalador pidiendo permiso, y es lo esperado.",
                    terminal: "Entra a **nodejs.org** y descarga la versión que dice **LTS**. Corre el instalador y acepta todo por defecto. Después cierra tu terminal por completo y abre una nueva.",
                },
                note: {
                    claudecode: "Sáltate esto si la revisión de arriba ya te dio un número de versión.",
                    terminal: "Cerrar y volver a abrir la terminal no es opcional: una terminal que ya estaba abierta no puede ver un programa instalado después de que ella arrancó, y esa es la razón número uno de que el paso siguiente diga «command not found» en una máquina donde Node se instaló perfecto.",
                },
            },
            {
                id: "install-cli",
                icon: "🧰",
                title: "Instala la herramienta de Firebase",
                body: {
                    claudecode: "Pídele: **instala el CLI de Firebase**. Ejecuta `npm install -g firebase-tools`, que es un solo comando que descarga la herramienta que publica tu app.",
                    terminal: "En tu terminal, escribe esto y presiona Enter:\n\n```\nnpm install -g firebase-tools\n```\n\nImprime un montón de líneas y tarda uno o dos minutos. Es normal.",
                },
                note: {
                    claudecode: "Si falla por permisos, díselo y deja que lea el error: este es justo el tipo de problema específico de cada máquina en el que es bueno, y adivinarle tú solo es como se te va una tarde.",
                    terminal: "En Windows, si te sale un mensaje sobre scripts deshabilitados, copia el error completo y pégaselo a Claude aquí abajo. En Mac, si menciona `EACCES` o permiso denegado, haz lo mismo. Los dos son normales, los dos se arreglan, y a ninguno vale la pena adivinarle.",
                },
            },
            {
                id: "login",
                icon: "🔐",
                title: "Inicia sesión",
                body: {
                    claudecode: "Pídele: **corre firebase login**. Se abre una ventana del navegador; elige tu cuenta de Google y apruébala.",
                    terminal: "Escribe `firebase login` y presiona Enter. Se abre una ventana del navegador; elige tu cuenta de Google y apruébala.",
                },
                note: "Usa **la misma cuenta de Google** con la que creaste el proyecto en la lección pasada. Entrar con otra no da error y se ve de lo más normal — nada más que no vas a poder ver tu proyecto, y el paso siguiente es donde te enteras.",
            },
            {
                id: "verify",
                icon: "✅",
                title: "Comprueba que sirvió",
                body: {
                    claudecode: "Pídele: **lista mis proyectos de Firebase**. Te regresa el nombre de tu proyecto y su Project ID en una tablita.",
                    terminal: "Escribe `firebase projects:list` y presiona Enter. Te regresa el nombre de tu proyecto y su Project ID en una tablita.",
                },
                note: "Compara el Project ID de esa tabla con el que anotaste en la lección pasada. Tienen que ser iguales. Si tu proyecto no aparece, entraste con la cuenta de Google equivocada: corre `firebase logout` y vuelve a iniciar sesión.",
            },
        ],
        exercise: "Instala la herramienta de Firebase e inicia sesión, usando la lista que está junto a esta lección. Elige arriba si Claude Code ejecuta los comandos o si los escribes tú. Terminas cuando en la terminal se imprima una lista de tus proyectos de Firebase con tu Project ID adentro.",
        hint: "Casi todo lo que sale mal aquí es una de tres cosas: una terminal que no reiniciaste después de instalar Node, un error de permisos en la instalación, o haber entrado con la cuenta de Google equivocada. Copia el error exacto y pégaselo a Claude aquí abajo: los resuelve rápido, y ninguno vale una tarde.",
    },
};

const PUBLISH_WITH_FIREBASE = {
    en: {
        title: "Your publishing checklist",
        allDone: "All done — your app is live, and you sent someone the link.",
        intro: "Tick each one as you actually do it. Your progress is saved, so you can leave, do the step, and come back to this page.",
        selfAttested: "Nothing here is checked automatically — this list is yours to keep track with, and you tick it honestly.",
        osLabel: "How are you doing this?",
        alreadyDone: "My app is already live at its own address",
        alreadyDoneHint: "Ticks everything. Only use it if you have opened your app from its web.app address in a browser.",
        gateNote: "Settle the folder first. Every command below runs against wherever you happen to be, and being in the wrong place is the whole of what goes wrong here.",
        os: [
            { id: "claudecode", label: "Claude Code does it for me", icon: "🤖" },
            { id: "terminal", label: "I'll type the commands", icon: "⌨️" },
        ],
        steps: [
            {
                id: "folder",
                icon: "📁",
                gate: true,
                title: "Be in the right folder",
                body: {
                    claudecode: "It is the folder Claude Code already has open — the one from Module 4. Ask it: **what files are in this project?** and check `index.html` and `firebase.json` are both in the list.",
                    terminal: "In your terminal, move into your app folder with `cd` and the path to it. Then list what is there — `ls` on a Mac, `dir` on Windows — and check `index.html` and `firebase.json` are both in the list.",
                },
                note: "`firebase.json` came with your project download and it is already set up correctly. It is the file that tells Firebase your app is the whole folder. If it is missing, ask Claude to write you one that serves the current folder.",
            },
            {
                id: "noinit",
                icon: "🛑",
                title: "Do NOT run firebase init",
                body: "Every tutorial you will find online starts with `firebase init`. Skip it. You already have the one file it would create, and running it can destroy your work.",
                note: "This is not caution for its own sake. `firebase init hosting` asks **\"File index.html already exists. Overwrite?\"** — and a student who says yes has just replaced their app with Firebase's welcome page. It also silently resets `firebase.json`. There is nothing in it you need.",
            },
            {
                id: "deploy",
                icon: "🚀",
                title: "Publish it",
                body: {
                    claudecode: "Ask, in your own words: **deploy this to Firebase Hosting, project YOUR-PROJECT-ID** — using the ID you wrote down two lessons ago.",
                    terminal: "Type this, with your own Project ID in place of the capitals, and press Enter:\n\n```\nfirebase deploy --project YOUR-PROJECT-ID\n```",
                },
                note: {
                    claudecode: "It runs `firebase deploy --project ...`. Naming the project in the command is why no setup step was needed — that flag is what `firebase init` would otherwise have written into a file.",
                    terminal: "`--project` is the important half. It tells the command which project to publish to, which is why you never had to run any setup. If it says the project was not found, the ID is wrong — check it against `firebase projects:list`.",
                },
            },
            {
                id: "open",
                icon: "🌍",
                title: "Open it",
                body: "When it finishes it prints **Hosting URL:** and an address ending in `.web.app`. Click it.\n\nThat is your app, loading from the internet — not from a file on your computer, not from inside this course.",
                note: "Unlike some hosts there is no waiting: it is live the moment the command finishes. If you see a Firebase welcome page instead of your app, `index.html` was not in the folder you deployed from — go back to the first step.",
            },
            {
                id: "phone",
                icon: "📱",
                title: "Open it on your phone",
                body: "Type the address into your phone's browser, or send yourself the link.\n\nDo this properly, because it is the version your customers will use. Read it as a stranger would.",
                note: "Anything too small, anything cut off at the edge, anything you have to pinch to read is a real problem with a real fix — screenshot it, paste it to Claude, and ask.",
            },
            {
                id: "send",
                icon: "💌",
                title: "Send it to one person",
                body: "Not \"share it later\". Now, to one actual human — a friend, a family member, a customer.",
                note: "You wrote a spec on day one, you built an app, and you just put it on the internet at an address you own. Sending it is how that stops being a course exercise and becomes a thing that exists.",
            },
        ],
        exercise: "Publish your app with the checklist beside this lesson, then open it on your phone and send the link to one person. Choose at the top whether Claude Code is running the commands or you are typing them.",
        hint: "If the live page shows a Firebase welcome screen, you deployed a folder with no `index.html` in it. If the command says the project was not found, your Project ID is wrong — run `firebase projects:list` and copy it exactly. And if you ever get asked whether to overwrite `index.html`, the answer is no, always.",
    },

    es: {
        title: "Tu lista de publicación",
        allDone: "Listo: tu app está en vivo y ya le mandaste el link a alguien.",
        intro: "Ve marcando cada paso conforme lo haces de verdad. Tu progreso se guarda, así que puedes salir, hacer el paso y volver a esta página.",
        selfAttested: "Aquí no se revisa nada automáticamente: esta lista es tuya, para llevar la cuenta, y la marcas con honestidad.",
        osLabel: "¿Cómo lo vas a hacer?",
        alreadyDone: "Mi app ya está en vivo en su propia dirección",
        alreadyDoneHint: "Marca todo. Úsalo solo si ya abriste tu app desde su dirección .web.app en un navegador.",
        gateNote: "Resuelve primero lo de la carpeta. Todos los comandos de abajo se ejecutan donde quiera que estés parado, y estar en el lugar equivocado es todo lo que sale mal aquí.",
        os: [
            { id: "claudecode", label: "Claude Code lo hace por mí", icon: "🤖" },
            { id: "terminal", label: "Yo escribo los comandos", icon: "⌨️" },
        ],
        steps: [
            {
                id: "folder",
                icon: "📁",
                gate: true,
                title: "Párate en la carpeta correcta",
                body: {
                    claudecode: "Es la carpeta que Claude Code ya tiene abierta, la del Módulo 4. Pregúntale: **¿qué archivos hay en este proyecto?** y revisa que `index.html` y `firebase.json` estén los dos en la lista.",
                    terminal: "En tu terminal, métete a la carpeta de tu app con `cd` y la ruta. Luego lista lo que hay —`ls` en Mac, `dir` en Windows— y revisa que `index.html` y `firebase.json` estén los dos en la lista.",
                },
                note: "`firebase.json` vino con la descarga de tu proyecto y ya está bien configurado. Es el archivo que le dice a Firebase que tu app es la carpeta completa. Si no está, pídele a Claude que te escriba uno que sirva la carpeta actual.",
            },
            {
                id: "noinit",
                icon: "🛑",
                title: "NO corras firebase init",
                body: "Todos los tutoriales que vas a encontrar en internet empiezan con `firebase init`. Sáltatelo. Ya tienes el único archivo que ese comando crearía, y correrlo puede destruir tu trabajo.",
                note: "Esto no es precaución por precaución. `firebase init hosting` pregunta **«File index.html already exists. Overwrite?»** — y quien contesta que sí acaba de reemplazar su app con la página de bienvenida de Firebase. Además resetea `firebase.json` sin avisar. No hay nada ahí que necesites.",
            },
            {
                id: "deploy",
                icon: "🚀",
                title: "Publícala",
                body: {
                    claudecode: "Pídeselo con tus palabras: **publica esto en Firebase Hosting, proyecto TU-PROJECT-ID** — con el ID que anotaste hace dos lecciones.",
                    terminal: "Escribe esto, con tu propio Project ID en lugar de las mayúsculas, y presiona Enter:\n\n```\nfirebase deploy --project TU-PROJECT-ID\n```",
                },
                note: {
                    claudecode: "Ejecuta `firebase deploy --project ...`. Nombrar el proyecto en el comando es la razón por la que no hizo falta ningún paso de configuración: esa bandera es lo que `firebase init` habría escrito en un archivo.",
                    terminal: "`--project` es la mitad importante. Le dice al comando en qué proyecto publicar, y por eso nunca tuviste que correr ninguna configuración. Si dice que no encontró el proyecto, el ID está mal: compáralo con `firebase projects:list`.",
                },
            },
            {
                id: "open",
                icon: "🌍",
                title: "Ábrela",
                body: "Cuando termina imprime **Hosting URL:** y una dirección que acaba en `.web.app`. Haz clic ahí.\n\nEsa es tu app, cargando desde internet: no desde un archivo de tu computadora, no desde adentro de este curso.",
                note: "A diferencia de otros servicios, aquí no hay que esperar: está en vivo en cuanto termina el comando. Si en lugar de tu app ves una página de bienvenida de Firebase, `index.html` no estaba en la carpeta desde la que publicaste — regrésate al primer paso.",
            },
            {
                id: "phone",
                icon: "📱",
                title: "Ábrela en tu teléfono",
                body: "Escribe la dirección en el navegador de tu teléfono, o mándate el link.\n\nHazlo bien, porque esta es la versión que van a usar tus clientes. Léela como la leería un desconocido.",
                note: "Todo lo que se vea muy chiquito, lo que quede cortado en la orilla, lo que tengas que agrandar con los dedos para leer, es un problema real con un arreglo real: tómale captura, pégasela a Claude y pregúntale.",
            },
            {
                id: "send",
                icon: "💌",
                title: "Mándasela a una persona",
                body: "No «luego la comparto». Ahorita, a un ser humano de verdad: un amigo, alguien de tu familia, un cliente.",
                note: "Escribiste un plan el primer día, construiste una app y acabas de ponerla en internet en una dirección que es tuya. Mandarla es lo que hace que eso deje de ser un ejercicio del curso y se vuelva una cosa que existe.",
            },
        ],
        exercise: "Publica tu app con la lista que está junto a esta lección, después ábrela en tu teléfono y mándale el link a una persona. Elige arriba si Claude Code ejecuta los comandos o si los escribes tú.",
        hint: "Si la página en vivo muestra una pantalla de bienvenida de Firebase, publicaste una carpeta sin `index.html` adentro. Si el comando dice que no encontró el proyecto, tu Project ID está mal: corre `firebase projects:list` y cópialo igualito. Y si alguna vez te preguntan si quieres sobrescribir `index.html`, la respuesta es no, siempre.",
    },
};

const SHIP_A_CHANGE_FIREBASE = {
    en: {
        title: "Your shipping checklist",
        allDone: "All done — you shipped a change, and you undid one.",
        intro: "Tick each one as you actually do it. Your progress is saved, so you can leave, do the step, and come back to this page.",
        selfAttested: "Nothing here is checked automatically — this list is yours to keep track with, and you tick it honestly.",
        osLabel: "How are you doing this?",
        alreadyDone: "I've shipped a change and rolled one back",
        alreadyDoneHint: "Ticks everything. Only use it if you have actually seen a change go live and then seen it come back out.",
        gateNote: "Make a real change, not a pretend one. The point of this lesson is the loop, and a loop you ran on a fake edit is not one you will trust on a real one.",
        os: [
            { id: "claudecode", label: "Claude Code does it for me", icon: "🤖" },
            { id: "terminal", label: "I'll type the commands", icon: "⌨️" },
        ],
        steps: [
            {
                id: "change",
                icon: "✏️",
                gate: true,
                title: "Change one real thing",
                body: {
                    claudecode: "Pick something small you actually want different — a heading, a colour, a button's words — and ask Claude Code for it in your own words.",
                    terminal: "Pick something small you actually want different — a heading, a colour, a button's words — and edit the file yourself, or ask Claude for the change and paste it in.",
                },
                note: "Small on purpose. You are learning the loop this time, not the change, and a big edit makes it harder to tell which of the two broke.",
            },
            {
                id: "redeploy",
                icon: "🚀",
                title: "Publish it again",
                body: {
                    claudecode: "Ask: **deploy this to Firebase again**. Same command as last lesson, same project.",
                    terminal: "The same command as last lesson, exactly:\n\n```\nfirebase deploy --project YOUR-PROJECT-ID\n```",
                },
                note: "This is the entire deploy step, every time, forever. It is not a shorter version of something longer — publishing an update genuinely is the same one command as publishing the first time.",
            },
            {
                id: "refresh",
                icon: "🔄",
                title: "Refresh the live page and find your change",
                body: "Go to your `.web.app` address and reload. Your change is there.",
                note: "If it still looks old, that is your browser holding onto the old files, not Firebase. Reload while clearing the cache: **Ctrl+Shift+R**, or **Cmd+Shift+R** on a Mac.",
            },
            {
                id: "rollback",
                icon: "⏪",
                title: "Now break it on purpose, and undo it",
                body: "Change something obviously wrong — delete your main heading — and deploy that. Look at the live site and see it broken.\n\nThen open the Firebase console, go to **Hosting**, and find the list of releases. Your previous version is right there. Roll back to it.",
                note: "Do this now, on purpose, while it is a game. The first time you need it, it will be an accident, you will be upset, and you will not be reading a lesson.",
            },
            {
                id: "loop",
                icon: "🔁",
                title: "Know what you actually have",
                body: "Change a file, run one command, refresh. That is the loop, and it does not get more complicated than this — not for a bigger app, not for a real business, not ever.",
                note: "And when a change goes wrong, the last good version is one click away in the console. That is the part most people building their first app do not have, and it is why breaking it on purpose was worth five minutes.",
            },
        ],
        exercise: "Ship one small real change to your live app using the checklist beside this lesson, then break something on purpose and roll it back from the Firebase console. Both halves matter: the second one is why the first is safe.",
        hint: "If the live site still shows the old version after a minute, reload while clearing the cache — Ctrl+Shift+R, or Cmd+Shift+R on a Mac. Browsers hold onto old files longer than Firebase does.",
    },

    es: {
        title: "Tu lista para publicar cambios",
        allDone: "Listo: publicaste un cambio y deshiciste otro.",
        intro: "Ve marcando cada paso conforme lo haces de verdad. Tu progreso se guarda, así que puedes salir, hacer el paso y volver a esta página.",
        selfAttested: "Aquí no se revisa nada automáticamente: esta lista es tuya, para llevar la cuenta, y la marcas con honestidad.",
        osLabel: "¿Cómo lo vas a hacer?",
        alreadyDone: "Ya publiqué un cambio y ya deshice uno",
        alreadyDoneHint: "Marca todo. Úsalo solo si de verdad viste un cambio salir en vivo y después lo viste desaparecer.",
        gateNote: "Haz un cambio de verdad, no uno de mentiras. Lo que importa de esta lección es el ciclo, y un ciclo que corriste sobre un cambio falso no es uno en el que vayas a confiar cuando sea real.",
        os: [
            { id: "claudecode", label: "Claude Code lo hace por mí", icon: "🤖" },
            { id: "terminal", label: "Yo escribo los comandos", icon: "⌨️" },
        ],
        steps: [
            {
                id: "change",
                icon: "✏️",
                gate: true,
                title: "Cambia una cosa de verdad",
                body: {
                    claudecode: "Escoge algo chico que de verdad quieras distinto —un título, un color, las palabras de un botón— y pídeselo a Claude Code con tus palabras.",
                    terminal: "Escoge algo chico que de verdad quieras distinto —un título, un color, las palabras de un botón— y edita el archivo tú, o pídele el cambio a Claude y pégalo.",
                },
                note: "Chico a propósito. Esta vez estás aprendiendo el ciclo, no el cambio, y una edición grande hace más difícil saber cuál de los dos se rompió.",
            },
            {
                id: "redeploy",
                icon: "🚀",
                title: "Publícala otra vez",
                body: {
                    claudecode: "Pídele: **vuelve a publicar esto en Firebase**. El mismo comando de la lección pasada, el mismo proyecto.",
                    terminal: "El mismo comando de la lección pasada, igualito:\n\n```\nfirebase deploy --project TU-PROJECT-ID\n```",
                },
                note: "Este es todo el paso de publicación, cada vez, para siempre. No es una versión corta de algo más largo: publicar una actualización de verdad es el mismo comando que publicar la primera vez.",
            },
            {
                id: "refresh",
                icon: "🔄",
                title: "Recarga la página en vivo y busca tu cambio",
                body: "Entra a tu dirección `.web.app` y recarga. Ahí está tu cambio.",
                note: "Si se sigue viendo igual que antes, es tu navegador aferrado a los archivos viejos, no Firebase. Recarga limpiando la caché: **Ctrl+Shift+R**, o **Cmd+Shift+R** en Mac.",
            },
            {
                id: "rollback",
                icon: "⏪",
                title: "Ahora rómpela a propósito, y deshazlo",
                body: "Cambia algo obviamente mal —borra tu título principal— y publica eso. Entra al sitio en vivo y velo roto.\n\nDespués abre la consola de Firebase, ve a **Hosting** y busca la lista de publicaciones. Tu versión anterior está ahí mismo. Regrésate a ella.",
                note: "Hazlo ahorita, a propósito, mientras es un juego. La primera vez que lo necesites va a ser un accidente, vas a estar de malas, y no vas a estar leyendo una lección.",
            },
            {
                id: "loop",
                icon: "🔁",
                title: "Ten claro lo que ya tienes",
                body: "Cambias un archivo, corres un comando, recargas. Ese es el ciclo, y no se pone más complicado que esto: ni para una app más grande, ni para un negocio de verdad, ni nunca.",
                note: "Y cuando un cambio salga mal, la última versión buena está a un clic en la consola. Esa es la parte que la mayoría de la gente que construye su primera app no tiene, y por eso valió cinco minutos romperla a propósito.",
            },
        ],
        exercise: "Publica un cambio chico y real en tu app en vivo usando la lista que está junto a esta lección, y después rompe algo a propósito y regrésalo desde la consola de Firebase. Las dos mitades importan: la segunda es la razón por la que la primera es segura.",
        hint: "Si después de un minuto el sitio en vivo sigue mostrando la versión vieja, recarga limpiando la caché: Ctrl+Shift+R, o Cmd+Shift+R en Mac. Los navegadores se quedan con los archivos viejos más tiempo que Firebase.",
    },
};

export const AUTHORED_CHECKLISTS = {
    // CD02 Module 5 — shipping.
    V2511: GITHUB_PUSH,
    V2513: PUBLISH_WITH_PAGES,
    V2514: SHIP_A_CHANGE,
    // CD02 Module 6 — Claude Code.
    V2602: INSTALL_CLAUDE_CODE,
    V2611: PROJECT_SETUP,

    // CD04 — the same five checklists under CD04's ids, and the same objects, not
    // copies. `buildAuthoredChecklist` must keep handing back a module-level
    // reference (InstallChecklist keys a state-loading effect on its identity), and
    // aliasing preserves that for free. It also keeps the two-way `claudecode` /
    // `browser` branch identical across both courses, which the tests assert:
    // a path that existed in one shipping lesson and vanished in the next would
    // strand anyone on the browser side halfway to a live app.
    //
    // In CD04 the Claude Code module comes BEFORE shipping, so by the time a
    // student reaches V4502 the `claudecode` branch is the one they were just
    // taught. The `browser` branch still matters: Module 4 is self-attested and
    // Claude Code needs a paid Claude plan, so some students arrive without it.
    V4402: INSTALL_CLAUDE_CODE,
    // NOT PROJECT_SETUP — see the note above PROJECT_SETUP_V4. Same two branches
    // and the same steps, but CD04's zip comes from the build spec lesson earlier in
    // this module, not from a Web Lab button in a module CD04 does not have.
    V4405: PROJECT_SETUP_V4,

    // CD04 Module 5 — shipping, and NOT aliases: CD04 deploys with Firebase Hosting
    // where CD02 used GitHub Pages, so these four are its own. GITHUB_PUSH,
    // PUBLISH_WITH_PAGES and SHIP_A_CHANGE above are now CD02's alone; CD04 aliased them
    // until 2026-08-10, when the Firebase module was chosen over the GitHub one and the
    // latter was retired from the course.
    //
    // The ids skip V4502-V4505 because these lessons were built alongside the GitHub
    // module and keep the ids their content and narration were generated under. See the
    // note above MODULE_5 in devV4LessonsRegistry.js.
    //
    // V4511 and V4516 need no entry: they are the hotspot and promptLab lessons that
    // open and close the module.
    V4512: FIREBASE_PROJECT,
    V4513: INSTALL_FIREBASE_CLI,
    V4514: PUBLISH_WITH_FIREBASE,
    V4515: SHIP_A_CHANGE_FIREBASE,
};

/**
 * Resolve the authored checklist for a lesson in the requested locale.
 * Falls back to English, then null when nothing is authored.
 *
 * @param {string} lessonID  e.g. "V2602"
 * @param {string} locale    bare locale, e.g. "en" | "es"
 * @returns {object|null}
 */
export function buildAuthoredChecklist(lessonID, locale) {
    const entry = AUTHORED_CHECKLISTS[lessonID];
    if (!entry) return null;
    return entry[locale] || entry.en || null;
}

/** The steps that apply to one platform, in order. */
export function stepsForOs(checklist, osId) {
    if (!checklist) return [];
    return checklist.steps.filter((s) => !s.os || s.os.includes(osId));
}

/**
 * One of a step's text fields for one platform, whether or not it branches.
 *
 * `title`, `body` and `note` may each be a plain string or a per-platform map. V2611
 * needs all three to branch: on the `have` path step 2 is "unzip it", on `fresh` it is
 * "make an empty folder" — different actions, so a shared title and a shared warning
 * would each be wrong on one of the two paths.
 */
export function stepText(step, field, osId) {
    const value = step?.[field];
    if (!value) return "";
    return typeof value === "string" ? value : value[osId] || "";
}

/** A step's body text for one platform. Thin wrapper kept for existing callers. */
export function stepBody(step, osId) {
    return stepText(step, "body", osId);
}

export default AUTHORED_CHECKLISTS;
